import React, { useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import {
  TableFilterSelect,
  InputContainer,
  Typography,
  Button,
  BelongsToFilterSelects,
} from '../../design-system';
import { containerConditionOptions, containerTypes, INPUT_ERRORS } from '../../const';
import { i18n } from '../../i18n';
import { extendContainerValidator } from '../../utils';
import { useSaveFilters, useStatesWithFilterCounter } from '../../hooks';
import { CloseIconButton, DashboardHeader } from '../dashboard';

interface FiltersPopupProps {
  isOpen: boolean;
}

const FiltersPopup = styled.div<FiltersPopupProps>`
  display: grid;
  grid-template-rows: max-content 1fr;

  height: 100%;
  width: 100%;
  background-color: #f5f6fa;

  padding: var(--spacing-3) var(--spacing-6) var(--spacing-6);
  grid-row-gap: var(--spacing-3);

  position: absolute;
  top: 0;
  right: -100%;
  transition: right 0.3s;
  z-index: 1000;

  ${(props) =>
    props.isOpen &&
    css`
      right: 0;
    `}
`;

const FiltersInputs = styled.div`
  display: grid;
  grid-auto-rows: max-content;
  grid-gap: var(--spacing-6);

  padding-right: var(--spacing-6);
  margin-right: calc(var(--spacing-6) * -1);
  overflow-y: auto;
`;

interface Props extends FiltersPopupProps {
  country?: string;
  city?: string;
  yardId?: string;
  clientId?: string;
  number?: string;
  type?: string;
  condition?: string;
  setIsOpen: any;
  setStates: any;
  states: any;
}

export const MobileFilters: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  setStates,
  states,
  ...filteredStates
}: Props) => {
  const [oldFilters, setOldFilters] = useSaveFilters('belongs-to', true);
  const oldFiltersRef = useRef(oldFilters);
  const [filterStates, setFilterStates] = useStatesWithFilterCounter(
    {
      country: '',
      city: '',
      yardId: '',
      clientId: '',
      number: '',
      type: 'All',
      condition: 'All',
    },
    filteredStates,
  );
  const [number, setNumber] = useState('');
  const [type, setType] = useState('All');
  const [condition, setCondition] = useState('All');

  const [update, setUpdate] = useState(Math.random());

  const isFiltersChange =
    states.country !== filterStates.country ||
    states.city !== filterStates.city ||
    states.yardId !== filterStates.yardId ||
    states.number !== number ||
    states.type !== type ||
    states.condition !== condition;

  useEffect(() => {
    if (!isOpen) return;

    const oldFiltersData = {
      ...(filteredStates.country ? { country: filteredStates.country } : {}),
      ...(filteredStates.city ? { city: filteredStates.city } : {}),
      ...(filteredStates.yardId
        ? {
            yardId: filteredStates.yardId,
            yardName: oldFilters.yardName || oldFiltersRef.current.yardName,
          }
        : {}),
      ...(filteredStates.clientId
        ? {
            clientId: filteredStates.clientId,
            clientName: oldFilters.clientName || oldFiltersRef.current.clientName,
          }
        : {}),
    };
    setOldFilters(oldFiltersData);
    oldFiltersRef.current = oldFiltersData;
    setFilterStates('force', { ...states, ...filteredStates });
    setNumber(filteredStates.number || '');
    setType(filteredStates.type || 'All');
    setCondition(filteredStates.condition || 'All');

    setUpdate(Math.random());
  }, [isOpen]);

  const handleApplyFiltersClick = () => {
    setStates('force', {
      country: filterStates.country,
      city: filterStates.city,
      yardId: filterStates.yardId,
      number,
      type,
      condition,
    });
    setIsOpen(!isOpen);
  };

  return (
    <FiltersPopup isOpen={isOpen}>
      <DashboardHeader isSmallScreen={true}>
        <Typography variant={'textL'} weight={'bold'}>
          {i18n.t('pages.marketplace.filters')}
        </Typography>
        <CloseIconButton onClick={() => setIsOpen(false)} />
      </DashboardHeader>
      <FiltersInputs>
        <BelongsToFilterSelects
          key={update}
          handleChange={(obj) => {
            setFilterStates('force', obj);
          }}
          filtersType={'marketplace'}
        />
        <InputContainer
          label={i18n.t('label.containerNumber')}
          data-size={'medium'}
          value={number}
          onChange={(e) => {
            const value = e.target.value.toUpperCase();
            setNumber(value);
          }}
          helperText={
            number.length !== 0 && number.length < 3
              ? INPUT_ERRORS.NOT_VALID.CONTAINER_NUMBER_MIN
              : !extendContainerValidator(number)
              ? INPUT_ERRORS.NOT_VALID.CONTAINER_NUMBER_SHORT
              : ''
          }
          alwaysShowHelperText
        />
        <TableFilterSelect
          label={i18n.t('label.type')}
          options={containerTypes}
          value={type}
          onChange={(e) => setType(e.target.value)}
        />
        <TableFilterSelect
          label={i18n.t('label.condition')}
          options={containerConditionOptions}
          value={condition}
          onChange={(e) => setCondition(e.target.value)}
        />
        <Button size={'medium'} onClick={handleApplyFiltersClick} disabled={!isFiltersChange}>
          {i18n.t('button.applyFilters')}
        </Button>
      </FiltersInputs>
    </FiltersPopup>
  );
};
